import type { AABB, OBB, PolygonWithHoles, Segment, Vec2 } from '../../geo/types';
import type { PolygonIndex } from '../../geo/polygonIndex';
import { obbCorners, ringBounds } from '../../geo/polygon';
import { spansOverlapSegment, type BoxSpans } from '../../geo/sat';
import { createCellTable, type CellTable } from './cellTable';
import { bandRing, inBandedRing, type BandedRing } from './bandedRing';

// Lot planning asks the buildable blocks about half a million points; the site index checks
// every block's bounds for each and every edge of a block, so this lookup files the blocks by
// grid cell and each block's edges by height band first.
const CELL = 20;
// The site's buildable index files its edges in cells of this size, so a box meets the same edges here.
const EDGE_CELL = 20;
/* The box's bounds grow by far more than rounding and far less than any edge, so no edge the box touches is skipped. */
const PAD = 1e-6;

/** sat's projection of the box's corners on an axis: boxSpans without working the corners out again. */
function spanOf(corners: readonly Vec2[], axis: Vec2): { min: number; max: number } {
  let min = Infinity;
  let max = -Infinity;
  for (const c of corners) {
    const dot = c[0] * axis[0] + c[1] * axis[1];
    if (dot < min) min = dot;
    if (dot > max) max = dot;
  }
  return { min, max };
}

function boxesMeet(a: AABB, b: AABB): boolean {
  return a.maxX >= b.minX && a.minX <= b.maxX && a.maxY >= b.minY && a.minY <= b.maxY;
}

interface BandedPolygon {
  readonly outer: BandedRing;
  readonly holes: readonly BandedRing[];
}

function inBandedPolygon(p: Vec2, poly: BandedPolygon): boolean {
  if (!inBandedRing(p, poly.outer)) return false;
  for (const hole of poly.holes) if (inBandedRing(p, hole)) return false;
  return true;
}

/** Every edge of every ring, filed by the cells its bounds cross, as the site's index files them. */
interface FiledEdges {
  readonly edges: readonly Segment[];
  readonly bounds: readonly AABB[];
  readonly table: CellTable;
}

function fileEdges(polys: readonly PolygonWithHoles[]): FiledEdges {
  const edges = polys
    .flatMap((p) => [p.outer, ...p.holes])
    .flatMap((ring) => ring.map((a, i): Segment => ({ a, b: ring[(i + 1) % ring.length]! })));
  const bounds = edges.map(({ a, b }) => ({
    minX: Math.min(a[0], b[0]),
    minY: Math.min(a[1], b[1]),
    maxX: Math.max(a[0], b[0]),
    maxY: Math.max(a[1], b[1]),
  }));
  return { edges, bounds, table: createCellTable(bounds, EDGE_CELL) };
}

const UNSETTLED = -1;

/*
 * A cell no edge's bounds cross lies wholly inside a block or wholly outside all of them, so its
 * middle answers for every point in it; the rest ask the blocks.
 */
function settleCells(
  table: CellTable,
  polys: readonly PolygonWithHoles[],
  test: (cell: number, p: Vec2) => boolean
): Int8Array {
  const crossed = new Uint8Array(table.cellCount);
  for (const ring of polys.flatMap((p) => [p.outer, ...p.holes])) {
    ring.forEach((a, i) => {
      const b = ring[(i + 1) % ring.length]!;
      const box = { minX: Math.min(a[0], b[0]), minY: Math.min(a[1], b[1]), maxX: Math.max(a[0], b[0]), maxY: Math.max(a[1], b[1]) };
      table.forCells(box, (cell) => {
        crossed[cell] = 1;
      });
    });
  }
  return Int8Array.from(crossed, (hit, cell) => (hit ? UNSETTLED : Number(test(cell, table.middle(cell)))));
}

/** Whether a point lies in one of the blocks, as pointInPolygon answers block by block. */
export function buildableContains(polys: readonly PolygonWithHoles[]): (p: Vec2) => boolean {
  const bounds = polys.map((p) => ringBounds(p.outer));
  const table = createCellTable(bounds, CELL);
  const { start, items } = table;
  const banded = polys.map((p): BandedPolygon => ({ outer: bandRing(p.outer), holes: p.holes.map(bandRing) }));
  const ask = (cell: number, p: Vec2): boolean => {
    for (let k = start[cell]!; k < start[cell + 1]!; k++) {
      const i = items[k]!;
      const b = bounds[i]!;
      if (p[0] < b.minX || p[0] > b.maxX || p[1] < b.minY || p[1] > b.maxY) continue;
      if (inBandedPolygon(p, banded[i]!)) return true;
    }
    return false;
  };
  const settled = settleCells(table, polys, ask);

  return (p) => {
    const cell = table.cellAt(p[0], p[1]);
    if (cell < 0) return false;
    const answer = settled[cell]!;
    return answer === UNSETTLED ? ask(cell, p) : answer === 1;
  };
}

/**
 * The site's buildable index with a faster point test. It answers exactly as `index` does:
 * the same blocks are tested with the same point test, and boxes against the same edges.
 */
export function createBuildableLookup(polys: readonly PolygonWithHoles[], index: PolygonIndex): PolygonIndex {
  const contains = buildableContains(polys);
  const { edges, bounds, table } = fileEdges(polys);

  function obbInside(o: OBB): boolean {
    const corners = obbCorners(o);
    for (const corner of corners) {
      if (!contains(corner)) return false;
    }
    const box: AABB = {
      minX: Math.min(corners[0][0], corners[1][0], corners[2][0], corners[3][0]) - PAD,
      minY: Math.min(corners[0][1], corners[1][1], corners[2][1], corners[3][1]) - PAD,
      maxX: Math.max(corners[0][0], corners[1][0], corners[2][0], corners[3][0]) + PAD,
      maxY: Math.max(corners[0][1], corners[1][1], corners[2][1], corners[3][1]) + PAD,
    };
    const axisV: Vec2 = [-o.axisU[1], o.axisU[0]];
    const spans: BoxSpans = { corners, axisV, spanU: spanOf(corners, o.axisU), spanV: spanOf(corners, axisV) };
    /* An edge whose bounds miss the box's cannot meet the box, and the bounds test is the cheaper. */
    return !table.some(
      box,
      (i) => boxesMeet(bounds[i]!, box) && spansOverlapSegment(o, spans, edges[i]!.a, edges[i]!.b)
    );
  }

  return {
    contains,
    obbInside,
    edgesNear: (box) => index.edgesNear(box),
    nearestBoundaryPoint: (p, maxDistance) => index.nearestBoundaryPoint(p, maxDistance),
  };
}
